// src/pages/GuestLog.jsx
import { useState, useEffect } from 'react';
import { api } from '../api';
import { Card, PageHeader, Table, TR, TD, Badge, Btn, Modal, Flash, Spinner, Empty } from '../components/UI';

export default function GuestLog() {
  const [guests, setGuests]       = useState([]);
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(true);
  const [detail, setDetail]       = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [flagModal, setFlagModal] = useState(null);
  const [flagReason, setFlagReason] = useState('');
  const [flash, setFlash]         = useState(null);

  function load() {
    setLoading(true);
    api.get(`/api/guests?q=${encodeURIComponent(search)}`).then(r => {
      if (r?.ok) setGuests(r.data.guests || []);
      setLoading(false);
    });
  }

  useEffect(() => { load(); }, []);

  async function openDetail(g) {
    setDetail(g);
    setDetailData(null);
    const r = await api.get(`/api/guest/${g.id}/detail`);
    if (r?.ok) setDetailData(r.data);
  }

  async function flagGuest() {
    const r = await api.post(`/api/guest/${flagModal.id}/flag`, { reason: flagReason });
    if (r?.ok) { setFlash({ type: 'success', msg: 'Guest flagged.' }); setFlagModal(null); setFlagReason(''); load(); }
  }

  async function unflagGuest(id) {
    if (!window.confirm('Remove flag from this guest?')) return;
    const r = await api.post(`/api/guest/${id}/unflag`, {});
    if (r?.ok) { setFlash({ type: 'success', msg: 'Flag removed.' }); load(); }
  }

  async function deleteGuest(id, name) {
    if (!window.confirm(`Delete ${name} from the guest log? This removes all their visit history and cannot be undone.`)) return;
    const r = await api.post(`/api/guest/${id}/delete`, {});
    if (r?.ok) { setFlash({ type: 'success', msg: `${name} deleted.` }); setGuests(prev => prev.filter(g => g.id !== id)); }
  }

  return (
    <div style={{ padding: '20px 16px', maxWidth: 1100, margin: '0 auto' }}>
      <PageHeader title="Guest Log"
        actions={
          <a href={api.flaskUrl('/guests/export/csv')} style={{ padding: '7px 12px', fontSize: 12, fontWeight: 'bold', color: 'var(--accent)', border: '1px solid var(--accent)', textDecoration: 'none' }}>Export CSV</a>
        }
      />

      {flash && <Flash type={flash.type} onDismiss={() => setFlash(null)}>{flash.msg}</Flash>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input type="text" placeholder="Search name, address, city..." value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load()}
          style={{ padding: '7px 10px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--fg)', fontSize: 13, flex: 1 }} />
        <Btn onClick={load}>Search</Btn>
      </div>

      <Card>
        {loading ? <Spinner /> : guests.length === 0 ? <Empty message="No guests found." /> : (
          <Table headers={['Name', 'Address', 'City', 'State', 'ZIP', 'Visits', 'Last Seen', 'Status', 'Actions']}>
            {guests.map(g => (
              <TR key={g.id} onClick={() => openDetail(g)} highlight={g.flagged}>
                <TD style={{ fontWeight: 'bold', color: 'var(--accent)', cursor: 'pointer' }}>{g.last_name}, {g.first_name}</TD>
                <TD style={{ fontSize: 12 }}>{g.address || '—'}</TD>
                <TD style={{ fontSize: 12 }}>{g.city || '—'}</TD>
                <TD style={{ fontSize: 12 }}>{g.state || '—'}</TD>
                <TD style={{ fontSize: 12 }}>{g.zip || '—'}</TD>
                <TD style={{ fontWeight: 'bold', color: 'var(--fg)' }}>{g.visit_count}</TD>
                <TD style={{ fontSize: 12 }}>{g.last_seen || '—'}</TD>
                <TD>{g.flagged ? <Badge variant="red">Flagged</Badge> : <Badge variant="green">OK</Badge>}</TD>
                <TD>
                  <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                    {g.flagged
                      ? <Btn variant="ghost" style={{ fontSize: 10, padding: '3px 7px' }} onClick={() => unflagGuest(g.id)}>Remove Flag</Btn>
                      : <Btn variant="ghost" style={{ fontSize: 10, padding: '3px 7px' }} onClick={() => setFlagModal(g)}>Flag</Btn>}
                    <Btn variant="danger" style={{ fontSize: 10, padding: '3px 7px' }} onClick={() => deleteGuest(g.id, `${g.first_name} ${g.last_name}`)}>Delete</Btn>
                  </div>
                </TD>
              </TR>
            ))}
          </Table>
        )}
      </Card>

      {/* Detail modal */}
      {detail && (
        <Modal title={`${detail.last_name}, ${detail.first_name}`} onClose={() => setDetail(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16, fontSize: 13 }}>
            <div><div style={{ fontSize: 11, color: 'var(--fg3)' }}>ADDRESS</div><div>{detail.address || '—'}</div></div>
            <div><div style={{ fontSize: 11, color: 'var(--fg3)' }}>CITY</div><div>{detail.city || '—'}, {detail.state} {detail.zip}</div></div>
            <div><div style={{ fontSize: 11, color: 'var(--fg3)' }}>TOTAL VISITS</div><div style={{ fontWeight: 'bold', fontSize: 18 }}>{detail.visit_count}</div></div>
            <div><div style={{ fontSize: 11, color: 'var(--fg3)' }}>FIRST / LAST SEEN</div><div>{detail.first_seen} / {detail.last_seen}</div></div>
          </div>
          {detail.flagged && (
            <div style={{ background: '#fff0f0', border: '1px solid #fca5a5', padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#c00' }}>
              Flagged: {detail.flag_reason || 'No reason given'}
            </div>
          )}
          <div style={{ fontSize: 11, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--fg3)', marginBottom: 8 }}>Visited With</div>
          {!detailData ? <div style={{ color: 'var(--fg3)', fontSize: 13 }}>Loading...</div> :
           !detailData.groups?.length ? <div style={{ color: 'var(--fg3)', fontSize: 13 }}>No recorded visits linked to a member.</div> :
           detailData.groups.map((grp, i) => (
            <div key={i} style={{ border: '1px solid var(--border)', marginBottom: 8 }}>
              <div style={{ padding: '8px 12px', background: 'var(--bg2)', display: 'flex', justifyContent: 'space-between' }}>
                <div><strong>{grp.lastname}</strong> <span style={{ fontSize: 11, color: 'var(--fg3)' }}>#{grp.membership_number} · {grp.membership_type}</span></div>
                <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 'bold' }}>{grp.visits.length} visit{grp.visits.length !== 1 ? 's' : ''}</span>
              </div>
              <div style={{ padding: '6px 12px', fontSize: 12, color: 'var(--fg3)' }}>{grp.visits.join(', ')}</div>
            </div>
          ))}
        </Modal>
      )}

      {/* Flag modal */}
      {flagModal && (
        <Modal title={`Flag ${flagModal.first_name} ${flagModal.last_name}`} onClose={() => setFlagModal(null)}>
          <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--fg2)' }}>This guest will be blocked at the sign-in terminal.</div>
          <div style={{ marginBottom: 4, fontSize: 11, fontWeight: 'bold', color: 'var(--fg2)' }}>REASON (optional)</div>
          <textarea value={flagReason} onChange={e => setFlagReason(e.target.value)} rows={3}
            style={{ width: '100%', padding: '8px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--fg)', fontSize: 13, fontFamily: 'Arial, sans-serif', marginBottom: 12 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="danger" onClick={flagGuest}>Flag Guest</Btn>
            <Btn variant="ghost" onClick={() => setFlagModal(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
